import { Component, forwardRef, Input, OnDestroy, OnInit, signal, Type } from '@angular/core';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { createVariablesRecord, DoodBlockModel } from '@core/models/block.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { CamelCaseUtils } from '@app/utilities/camel-case.util';
import { DoodRenderingBlock } from '@shared/interfaces/block.interface';
import { ContentStoreSelector } from '@common/selectors/content.selector';
import { ALLOWED_BLOCKS } from '@config/blocks.config';
import { EvaluateBlockVisibilityService } from '@core/services/content-builder/evaluate-block-visibility.service';
import { Cb2PickBlocDirective } from '@core/directives/cb2-pick-bloc.directive';
import {
  CART_PAGE_PRESET_BLOCK_DEFAULT_BACKGROUND_COLOR,
  isCartPagePresetBlockWithFactoryBackground,
} from '@front-platform/content-builder';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ComponentFactoryComponent } from '../component-factory/component-factory.component';
import { shouldInstantiateBlockWhenHidden } from './instantiate-block-when-hidden.util';

@Component({
  selector: 'app-block-factory',
  templateUrl: './block-factory.component.html',
  styleUrls: ['./block-factory.component.scss'],
  standalone: true,
  imports: [forwardRef(() => ComponentFactoryComponent), Cb2PickBlocDirective, ContentBuilderColorPipe],
})
export class BlockFactoryComponent implements OnInit, OnDestroy {
  private readonly _destroyerRef = new Subject<boolean>();
  private readonly injectData$ = new BehaviorSubject<Record<string, unknown>>({});
  private lastMergedDataKey: string | null = null;

  /**
   * Bound into `app-component-factory`. Must be a signal: soft-refresh updates
   * often leave `isVisible` unchanged (`true` → `true`), so a plain field write
   * would not schedule change detection and the child would keep stale inputs.
   */
  protected readonly data = signal<Record<string, unknown>>({});
  protected readonly componentClass = signal<Type<unknown> | string | undefined>(undefined);

  // Resolved block id (string) once the block has been loaded from the store.
  // Exposed to the template so the CB2 pick-bloc overlay can report it back.
  resolvedBlockId: string | null = null;

  // injectData, is data coming from the component that open the modal
  @Input() set injectData(value: Record<string, unknown> | null | undefined) {
    this.injectData$.next(value ?? {});
  }
  @Input() blockId?: string | Partial<DoodRenderingBlock>;

  protected readonly isVisible = signal<boolean>(false);
  protected readonly instantiateWhenHidden = signal(false);
  protected readonly wrapCartPageColumnBlock = signal(false);
  protected readonly cartPageColumnBackgroundColor = signal<
    IContentBuilderFieldColor | undefined
  >(undefined);
  protected readonly cartPageColumnBackgroundDefault = CART_PAGE_PRESET_BLOCK_DEFAULT_BACKGROUND_COLOR;

  constructor(
    private blockSelector: ContentStoreSelector,
    private readonly evaluateBlockVisibilityService: EvaluateBlockVisibilityService,
  ) {}

  // TODO: Need to avoid modifying blockId input value
  ngOnInit(): void {
    if (!this.blockId) return;

    if (typeof this.blockId !== 'string') {
      if (this.blockId.componentClass) {
        this.data.set(this.blockId.data ?? {});
        this.componentClass.set(this.blockId.componentClass);
      }

      if (this.blockId.block) {
        this.blockId = this.blockId.block;
      }
    }

    this.resolvedBlockId = this.blockId.toString();

    combineLatest([this.blockSelector.selectBlock(this.blockId.toString()), this.injectData$])
      .pipe(
        map(([block, injectData]) => ({
          block,
          injectData,
          mergeKey: block
            ? JSON.stringify({
                blockData: block.data ?? {},
                injectData,
              })
            : '',
        })),
        distinctUntilChanged((left, right) => left.mergeKey === right.mergeKey),
        tap(({ block, injectData }) => {
          if (!block) return;
          this._renderComponent(block, injectData);
        }),
        switchMap(({ block }) => {
          if (!block) {
            return of(false);
          }
          const conditionalVisibility = block.data?.common?.conditionalVisibility;
          if (!conditionalVisibility || !conditionalVisibility.expression) {
            return of(true);
          }
          return this.evaluateBlockVisibilityService
            .evaluate$(conditionalVisibility.expression, {
              additionalContext: createVariablesRecord(block.data?.common?.variables ?? []),
            })
            .pipe(map(expressionResult => !!expressionResult));
        }),
        takeUntil(this._destroyerRef),
      )
      .subscribe(isVisible => {
        this.isVisible.set(isVisible);
      });
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  private _renderComponent(_descriptor: DoodBlockModel, injectData: Record<string, unknown>): void {
    if (!_descriptor) {
      console.error('[BlockFactory] Error: Block ' + this.blockId + ' is not found');
      return;
    }

    let _type = _descriptor.type;

    if (!_type.endsWith('Component')) {
      _type = _type + 'Component';
    }

    if (!ALLOWED_BLOCKS.hasOwnProperty(_type)) {
      console.error('[BlockFactory] Error: Component ' + _type + ' is not registered');
      return;
    }

    this.componentClass.set(Object(ALLOWED_BLOCKS)[_type]);
    this.instantiateWhenHidden.set(
      shouldInstantiateBlockWhenHidden(_type, _descriptor.data?.common?.conditionalVisibility),
    );
    this.wrapCartPageColumnBlock.set(isCartPagePresetBlockWithFactoryBackground(_type));
    if (!this.wrapCartPageColumnBlock()) {
      this.cartPageColumnBackgroundColor.set(undefined);
    } else {
      const camelCaseData =
        CamelCaseUtils.convertAttributeNamesToCamelCase(_descriptor.data ?? {}) ?? {};
      this.cartPageColumnBackgroundColor.set(
        camelCaseData['backgroundColor'] as IContentBuilderFieldColor | undefined,
      );
    }
    this.resolvedBlockId = _descriptor.id ?? this.resolvedBlockId;

    const baseData = {
      ...CamelCaseUtils.convertAttributeNamesToCamelCase(_descriptor.data),
      blockId: _descriptor.id,
    };
    const nextData = mergeBlockInjectData(baseData, injectData);

    const nextDataKey = JSON.stringify(nextData);
    if (nextDataKey === this.lastMergedDataKey) {
      return;
    }

    this.lastMergedDataKey = nextDataKey;
    this.data.set(nextData);
  }
}

export function mergeBlockInjectData(
  baseData: Record<string, unknown>,
  injectData: Record<string, unknown>,
): Record<string, unknown> {
  if (!injectData || Object.keys(injectData).length === 0) {
    return baseData;
  }

  const mergedInjectData = Object.fromEntries(
    Object.entries(injectData).filter(([, value]) => value !== undefined),
  );

  // Shop card name color: the child override wins over Shop cards injectData.
  const childEntityNameColor = baseData['entityNameColor'] ?? baseData['entity_name_color'];
  if (isCustomEntityNameColor(childEntityNameColor)) {
    delete mergedInjectData['entityNameColor'];
    delete mergedInjectData['entity_name_color'];
  }

  Object.entries(mergedInjectData).forEach(([key, value]) => {
    if (
      typeof baseData[key] === 'object' &&
      baseData[key] !== null &&
      typeof value === 'object' &&
      value !== null
    ) {
      mergedInjectData[key] = { ...(baseData[key] as object), ...(value as object) };
    }
  });

  return { ...baseData, ...mergedInjectData };
}

function isCustomEntityNameColor(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.length > 0;
  }

  if (!value || typeof value !== 'object') {
    return false;
  }

  const color = value as { type?: string; value?: unknown };
  if (!color.type || color.value == null || color.value === '') {
    return false;
  }

  return color.type.toLowerCase() !== ColorFieldTypesValues.Default;
}
